import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import styles from "../tablesStyle/WriteoffSection.module.css";
import CustomSelect from "../../customComponents/CustomSelect";
import WarehouseModal from "./customComponentsTable/WarehouseModal";
import PanelWriteOff from "./customComponentsTable/PanelWriteOff";
import { API_URL } from "../../config/api";

const WriteoffSection = () => {
  const [type, setType] = useState("");
  const [work, setWork] = useState("");
  const [product, setProduct] = useState("");
  const [warehouse, setWarehouse] = useState(null);

  const [storages, setStorages] = useState([]);
  const [products, setProducts] = useState([]);
  const [writeoffs, setWriteoffs] = useState([]);

  const [isWarehouseModalOpen, setIsWarehouseModalOpen] = useState(false);
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [selectedDocumentId, setSelectedDocumentId] = useState(null);

  const token = localStorage.getItem("token");

  const statusMap = {
    approved: "Подтверждено",
    pending: "В ожидании",
    rejected: "Отклонено",
    draft: "Черновик",
  };

  // ✅ useCallback для ESLint deps
  const fetchWriteoffs = useCallback(
    async (storageId) => {
      if (!token) return console.error("❌ Нет токена!");

      try {
        const url = storageId
          ? `${API_URL}/api/stock_changes?type=writeoff&storage_id=${storageId}`
          : `${API_URL}/api/stock_changes?type=writeoff`;

        const res = await axios.get(url, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (res.data?.data) {
          setWriteoffs(res.data.data);
          localStorage.setItem("writeoffs", JSON.stringify(res.data.data));
        }
      } catch (err) {
        console.error("Ошибка при загрузке списаний:", err);
        const saved = localStorage.getItem("writeoffs");
        if (saved) setWriteoffs(JSON.parse(saved));
      }
    },
    [token]
  );

  // ✅ useCallback для ESLint deps
  const fetchStorages = useCallback(async () => {
    if (!token) return console.error("❌ Нет токена!");

    try {
      const res = await axios.get(`${API_URL}/api/storages`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data?.data) {
        const mapped = res.data.data.map((storage) => ({
          label: storage.name,
          value: storage.id,
        }));
        setStorages(mapped);
        localStorage.setItem("storages", JSON.stringify(mapped));
      }
    } catch (err) {
      console.error("Ошибка при загрузке складов:", err);
      const saved = localStorage.getItem("storages");
      if (saved) setStorages(JSON.parse(saved));
    }
  }, [token]);

  // ✅ useCallback для ESLint deps
  const fetchProducts = useCallback(async () => {
    if (!token) return console.error("❌ Нет токена!");

    try {
      const res = await axios.get(`${API_URL}/api/products`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data?.data) {
        const mapped = res.data.data.map((prod) => ({
          label: prod.name_ru,
          value: prod.id,
        }));
        setProducts(mapped);
        localStorage.setItem("products", JSON.stringify(mapped));
      }
    } catch (err) {
      console.error("Ошибка при загрузке товаров:", err);
      const saved = localStorage.getItem("products");
      if (saved) setProducts(JSON.parse(saved));
    }
  }, [token]);

  // ✅ ESLint fix: добавили функции в deps
  useEffect(() => {
    fetchStorages();
    fetchProducts();
    fetchWriteoffs();
  }, [token, fetchStorages, fetchProducts, fetchWriteoffs]);

  const getStorageName = (id) => {
    const storage = storages.find((s) => s.value === id);
    return storage ? storage.label : "";
  };

  const handleDocumentClick = (item) => {
    const storageName = getStorageName(item.storage_id);

    setWarehouse({ id: item.storage_id, name: storageName });
    setSelectedDocumentId(item.id);
    setIsPanelOpen(true);

    console.log("Открыт документ:", item.id, "Склад:", item.storage_id, storageName);
  };

  const handleSelectWarehouse = (selected) => {
    setWarehouse({ id: selected.id, name: selected.name });
    setSelectedDocumentId(null);
    setIsPanelOpen(true);
  };

  const totalQuantity = writeoffs.reduce((sum, item) => sum + (Number(item.quantity) || 0), 0);
  const totalAmount = writeoffs.reduce((sum, item) => sum + (Number(item.paid_amount) || 0), 0);

  return (
    <div className={styles.container}>
      <div className={styles.blockMoney}>
        <div className={styles.blockMoneyOne}>
          <h3 className={styles.blockMoneyHead}>Итого количество списания</h3>
          <p className={styles.blockMoneySum}>{totalQuantity}</p>
        </div>
        <div className={styles.blockMoneyTwo}>
          <h3 className={styles.blockMoneyHead}>Итого сумма списания</h3>
          <p className={styles.blockMoneySum}>{totalAmount.toLocaleString()} ₸</p>
        </div>
      </div>

      <button
        className={styles.add}
        onClick={() => {
          setSelectedDocumentId(null);
          setIsWarehouseModalOpen(true);
        }}
      >
        +
      </button>

      <div className={styles.filters}>
        <CustomSelect
          className={styles.filters__select}
          options={storages}
          value={warehouse?.id || ""}
          onChange={(val) => {
            setWarehouse({ id: val, name: getStorageName(val) });
            fetchWriteoffs(val);
          }}
          placeholder="Склад"
        />

        <input type="date" name="date" className={styles.date} />

        <CustomSelect
          className={styles.filters__select}
          options={["Работник 1", "Работник 2", "Работник 3", "Работник 4"]}
          value={work}
          onChange={setWork}
          placeholder="Сотрудник"
        />

        <CustomSelect
          className={styles.filters__select}
          options={products}
          value={product}
          onChange={setProduct}
          placeholder="Поиск товара"
        />

        <CustomSelect
          className={styles.filters__select}
          options={["Статус 1", "Статус 2", "Статус 3", "Статус 4"]}
          value={type}
          onChange={setType}
          placeholder="Статус"
        />
      </div>

      <div className={styles.tableWrapper}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Номер документа</th>
              <th>Дата</th>
              <th>Склад</th>
              <th>Сотрудник</th>
              <th>Комментарий</th>
              <th>Статус</th>
            </tr>
          </thead>
          <tbody>
            {writeoffs.map((item) => (
              <tr key={item.id}>
                <td
                  className={styles.clickable}
                  onClick={() => handleDocumentClick(item)}
                  style={{ cursor: "pointer" }}
                >
                  {item.id}
                </td>
                <td>{new Date(item.inserted_at).toLocaleDateString()}</td>
                <td>{getStorageName(item.storage_id)}</td>
                <td>-</td>
                <td>{item.aux?.comment || "-"}</td>
                <td>{statusMap[item.status] || item.status}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {isPanelOpen && (
        <PanelWriteOff
          warehouse={warehouse}
          documentId={selectedDocumentId}
          isOpen={isPanelOpen}
          onClose={() => setIsPanelOpen(false)}
          refreshWriteoffs={fetchWriteoffs}
        />
      )}

      <WarehouseModal
        isOpen={isWarehouseModalOpen}
        onClose={() => setIsWarehouseModalOpen(false)}
        onSelect={handleSelectWarehouse}
      />
    </div>
  );
};

export default WriteoffSection;
